import { useCallback } from 'react';

import {
  Active,
  DataRef,
  DndContext,
  DndContextProps,
  Over,
  PointerSensor,
  PointerSensorOptions,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

export interface CustomActive extends Active {
  data: DataRef<{
    type: string;
  }>;
}

export interface CustomOver extends Over {
  data: DataRef<{
    accepts: string;
  }>;
}

export interface CustomDragEvent extends DragEvent {
  active: CustomActive;
  over: CustomOver | null;
}

export interface DragEndEvent extends CustomDragEvent {}

export interface DraggableContextProps extends DndContextProps {
  children?: React.ReactNode;
}

/**
 * Pointer sensor that ignores touch pointers.
 *
 * Touch input is handled by the `TouchSensor` (with its hold/tolerance
 * activation constraint so lists keep scrolling), while mouse and pen input
 * activate immediately through this sensor. Both sensors are always
 * registered: choosing one set based on `ontouchstart` / `maxTouchPoints`
 * left mouse users on touch-capable laptops without any drag sensor at all
 * (SPAZ2-3518).
 */
class NonTouchPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (
        { nativeEvent: event }: React.PointerEvent,
        { onActivation }: PointerSensorOptions
      ) => {
        if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0) {
          return false;
        }

        onActivation?.({ event });

        return true;
      },
    },
  ];
}

export const DraggableContext: React.FC<DraggableContextProps> = ({ children }) => {
  const sensors = useSensors(
    useSensor(NonTouchPointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 50, // pixels by which the touch input needs to be moved before a drag start event is emitted.
        delay: 100, // milliseconds that a draggable item needs to be held by the touch input before a drag start event is emitted.
        // pixels of motion that is tolerated before the drag operation is aborted.
        // the operation will only be aborted if the finger is moved by more than 5 pixels during the delay.
        // This is useful for scrolling on touch devices.
        tolerance: 5,
      },
    })
  );

  const handleDragEnd = useCallback<PickProp<DndContextProps, 'onDragEnd'>>((event) => {
    const { active, over } = event as any as CustomDragEvent;

    const accepts = over && over.data.current?.accepts;
    const type = active?.data?.current?.type;
    if (type && accepts && accepts.includes(type)) {
      // console.log('yeah');
    }
  }, []);

  const handleDragOver = useCallback<PickProp<DndContextProps, 'onDragOver'>>((event) => {
    const { active, over } = event as any as CustomDragEvent;

    const accepts = over && over.data.current?.accepts;
    const type = active?.data?.current?.type;
    if (type && accepts && accepts.includes(type)) {
      // console.log('over yeah');
    }
  }, []);

  return (
    <DndContext
      collisionDetection={pointerWithin}
      modifiers={[restrictToWindowEdges]}
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      {children}
    </DndContext>
  );
};
